import { useEffect, useRef, useState } from 'react';

/**
 * Hook to determine if component should animate on first mount
 * Only animates on:
 * - Page refresh
 * - New browser tab/window
 * - First visit to auth pages in session
 */
export function useFirstMountAnimation() {
    const [shouldAnimate, setShouldAnimate] = useState(false);
    const hasAnimatedRef = useRef(false);

    useEffect(() => {
        const hasNotAnimatedInSession =
            !sessionStorage.getItem('auth-page-animated');

        const isDevelopment = import.meta.env.DEV;

        const checks = {
            // Check if page was refreshed
            isPageRefresh: window.performance?.navigation?.type === 1,

            // Check using Navigation API (newer browsers)
            isReload:
                window.performance?.getEntriesByType?.('navigation')?.[0]
                    ?.type === 'reload',

            // Check if coming from external source or direct navigation
            isNewSession:
                !document.referrer ||
                new URL(document.referrer).origin !== window.location.origin,

            // Check if we haven't animated in this session
            hasNotAnimatedInSession,

            // Check if component hasn't animated in this render cycle
            hasNotAnimatedYet: !hasAnimatedRef.current,
            isDevelopment,
        };

        // In development, always animate for testing
        // In production, use the proper checks
        const shouldTriggerAnimation = isDevelopment
            ? hasNotAnimatedInSession && checks.hasNotAnimatedYet
            : (checks.isPageRefresh ||
                  checks.isReload ||
                  checks.isNewSession) &&
              checks.hasNotAnimatedInSession &&
              checks.hasNotAnimatedYet;

        if (shouldTriggerAnimation) {
            setShouldAnimate(true);
            hasAnimatedRef.current = true;
            sessionStorage.setItem('auth-page-animated', 'true');
        }
    }, []);

    // Cleanup session storage on page unload
    useEffect(() => {
        const handleBeforeUnload = () => {
            sessionStorage.removeItem('auth-page-animated');
        };

        // Also clear after period of inactivity
        const handleVisibilityChange = () => {
            if (document.hidden) {
                setTimeout(() => {
                    if (document.hidden) {
                        sessionStorage.removeItem('auth-page-animated');
                    }
                }, 30000); // 30 seconds for testing (was 60000)
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            document.removeEventListener(
                'visibilitychange',
                handleVisibilityChange,
            );
        };
    }, []);

    // For testing: add a manual trigger function (remove in production)
    const triggerAnimation = () => {
        sessionStorage.removeItem('auth-page-animated');
        setShouldAnimate(true);
    };

    // Expose triggerAnimation for debugging (remove in production)
    useEffect(() => {
        if (import.meta.env.DEV) {
            window.triggerLoginAnimation = triggerAnimation;
        }
    }, []);

    return shouldAnimate;
}
